import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { ApplicationInitStatus, inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../services/auth';
import { InactivityService } from '../services/inactivity.service';
import { PermisosService } from '../services/permisos.service';

// En estas rutas el 401 es respuesta del propio formulario (credenciales, contraseña actual
// incorrecta), no un token vencido o revocado: el componente muestra el error y la sesión sigue.
const RUTAS_401_SIN_CIERRE = ['login', 'forgot-password', 'reset-password', 'activar-cuenta', 'change-password']
  .map(ruta => `${environment.apiUrl}/auth/${ruta}`);

const MOTIVO_POR_DEFECTO = 'Tu sesión expiró. Inicia sesión nuevamente.';

// HU-PERM-9 C1: un 403 en una escritura significa que el perfil no tiene esa acción liberada
// (o que el recurso está fuera de su alcance). Aviso global corto, siempre el mismo texto: el
// detalle del back ("Sin permiso para este módulo", "No tienes acceso a esta obra"…) lo sigue
// mostrando cada pantalla que ya lo muestra, así no se repite el mismo mensaje dos veces. Los
// 403 de lectura (GET) no avisan: cada pantalla decide, y varias los usan para ocultar bloques.
const METODOS_ESCRITURA = ['POST', 'PUT', 'PATCH', 'DELETE'];
const AVISO_ACCION_BLOQUEADA = 'Acción bloqueada: tu perfil no tiene esta acción liberada.';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const token = authService.getToken();

  if (token) {
    const authReq = req.clone({
      setHeaders: { Authorization: `Bearer ${token}` }
    });
    const router     = inject(Router);
    const inactivity = inject(InactivityService);
    const initStatus = inject(ApplicationInitStatus);
    const permisos   = inject(PermisosService);

    return next(authReq).pipe(
      catchError((err: unknown) => {
        // Solo si el token rechazado sigue siendo el guardado: el primer 401 lo borra, así que los
        // demás requests que fallen en la misma tanda no repiten el cierre; tampoco se dispara si la
        // sesión ya se cerró por inactividad/logout o si el token se reemplazó (login, cambio de contraseña).
        if (err instanceof HttpErrorResponse && err.status === 401
            && !RUTAS_401_SIN_CIERRE.includes(req.url) && authService.getToken() === token) {
          // Token vencido o inválido llega con el 'Unauthorized' genérico de Passport (en inglés)
          const msg = err.error?.message;
          const motivo = typeof msg === 'string' && msg && msg !== 'Unauthorized' ? msg : MOTIVO_POR_DEFECTO;

          inactivity.stop();
          authService.clearSession();
          authService.motivoCierre.set(motivo);
          // Durante el app initializer (refreshMe) todavía no hubo navegación inicial: esa pasa por
          // authGuard, que ya redirige a login con returnUrl. Navegar acá la duplicaría.
          if (initStatus.done) {
            router.navigate(['/auth/login'], { queryParams: { returnUrl: router.url } });
          }
        }

        // Mismo toast que los accesos denegados por ruta (MainLayout). Si llegan varios 403 en la
        // misma tanda, denegarAccesoMensaje() solo reinicia el temporizador: no se apilan avisos.
        if (err instanceof HttpErrorResponse && err.status === 403
            && METODOS_ESCRITURA.includes(req.method) && req.url.startsWith(environment.apiUrl)) {
          permisos.denegarAccesoMensaje(AVISO_ACCION_BLOQUEADA);
        }
        return throwError(() => err);
      })
    );
  }

  return next(req);
};
